import { useState } from 'react';
import { EMAIL_PATTERN, LIMITS, passwordProblems } from '@msarat/shared';
import { Alert, AuthLayout, Field, PasswordField, SubmitButton } from '@msarat/theme/components';

import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.jsx';

/**
 * Parent registration.
 *
 * Collects the parent's own details only. No question here asks about the
 * child: the student profile is created afterwards, by the parent, in a
 * separate step where the purpose of each field is obvious.
 *
 * Validation runs in the browser for fast feedback and again on the server for
 * safety. The rules come from @msarat/shared so the two cannot disagree.
 */
export function ParentRegister({ onRegistered, onGoToLogin }) {
  const { t, language } = useI18n();
  const [values, setValues] = useState({ name: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (value) => {
    setValues((current) => ({ ...current, [key]: value }));
    // Clear the field's error as soon as the user edits it; leaving a stale
    // error under a field they are fixing is disheartening.
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  function validate() {
    const found = {};
    if (!values.name.trim()) found.name = t('valid.required');
    if (!EMAIL_PATTERN.test(values.email.trim())) found.email = t('valid.invalid_email');

    const problems = passwordProblems(values.password);
    if (problems.includes('too_short')) {
      found.password = t('valid.password_too_short', { min: LIMITS.password.min });
    } else if (problems.length > 0) {
      found.password = t('valid.password_invalid');
    }
    return found;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError('');

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setPending(true);
    try {
      await api.registerParent({
        name: values.name.trim(),
        email: values.email.trim(),
        password: values.password,
        language,
      });
      setDone(true);
      onRegistered?.(values.email.trim());
    } catch (error) {
      const fieldErrors = error.fieldErrors?.(t, { min: LIMITS.password.min }) ?? {};
      setErrors(fieldErrors);
      // Only show a form-level message when no field owns the problem,
      // otherwise the same error appears twice.
      if (Object.keys(fieldErrors).length === 0) setFormError(error.message);
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
      brandLabel={t('brand.name')}
        title={t('register.doneTitle')}
        subtitle={t('register.doneSubtitle', { email: values.email.trim() })}
      >
        <Alert kind="success">{t('register.doneSpam')}</Alert>
        <button className="button button--secondary button--block" type="button" onClick={onGoToLogin}>
          {t('register.backToLogin')}
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      brandLabel={t('brand.name')}
      title={t('register.title')}
      subtitle={t('register.subtitle')}
      footer={
        <>
          {t('register.haveAccount')}{' '}
          <a href="/login" onClick={(event) => { event.preventDefault(); onGoToLogin?.(); }}>
            {t('register.signIn')}
          </a>
        </>
      }
    >
      {/* noValidate: the browser's own bubbles are not translated and cannot be
          styled, so validation messages are rendered by the app instead. */}
      <form className="stack" onSubmit={handleSubmit} noValidate>
        <Alert kind="error">{formError}</Alert>

        <Field
          id="name"
          label={t('register.name')}
          value={values.name}
          onChange={set('name')}
          error={errors.name}
          required
          autoComplete="name"
          maxLength={LIMITS.parentName}
        />

        <Field
          id="email"
          label={t('register.email')}
          type="email"
          value={values.email}
          onChange={set('email')}
          error={errors.email}
          required
          autoComplete="email"
          inputMode="email"
          ltr
          maxLength={LIMITS.email}
        />

        <PasswordField
          id="password"
          label={t('register.password')}
          value={values.password}
          onChange={set('password')}
          error={errors.password}
          hint={t('register.passwordHint', { min: LIMITS.password.min })}
          required
          autoComplete="new-password"
          showLabel={t('common.showPassword')}
          hideLabel={t('common.hidePassword')}
        />

        <SubmitButton pending={pending}>{t('register.submit')}</SubmitButton>

        <p className="field__hint">{t('register.childNote')}</p>
      </form>
    </AuthLayout>
  );
}
